// Dependencies
import axios from 'axios'

// Services
import { accessHeaders } from "../../lib/cookie"
import { LocationDetail } from './project-address'

// Utils
import {
  ClientUtility as Client
} from '../utils/client-utility'

import {
  ConvertUtility as Convert
} from '../utils/convert-utility'

/**
 * Home location properties
 * @typedef HomeLocation
 * @type {Object}
 * @property {String} addressNo   - Address number
 * @property {String} addressRoad - Address road
 * @property {String} [soi]       - Soi
 * @property {String} [moo]       - Moo
 * @typedef {LocationDetail & HomeLocation} HomeAddress
 */

/**
 * Home create parameters
 * @typedef HomeCreate
 * @type {Object}
 * @property {String} projectCode   - Project code
 * @property {String} structure     - Construction type
 * @property {Number} area          - Useable area
 * @property {HomeAddress} address  - Home address details
 * @property {String} transferDate  - Transfer date
 */

/**
 * Home creation result properties
 * @typedef CreateResult
 * @type {Object}
 * @property {String} id        - Home ID 
 * @property {String} addressNo - Address number
 * @property {Date} createdAt   - Creation time
 */

/**
 * Home project detail properties
 * @typedef HomeProject
 * @type {Object}
 * @property {String} id   - Project ID
 * @property {String} name - Project name
 * @property {String} code - Project code
 * @property {String} icon - Project icon image URL
 */

/**
 * Home structure properties
 * @typedef HomeStructure
 * @type {Object}
 * @property {String} name - Construction code
 * @property {String} code - Construction name
 */

/**
 * Home detail properties
 * @typedef HomeDetail
 * @type {Object}
 * @property {Number} id                - Home ID
 * @property {Number} area              - Useable area
 * @property {HomeProject} project      - Project detail
 * @property {HomeStructure} structure  - Structure detail
 * @property {HomeAddress} address      - Home address details
 * @property {Date} transferDate        - Transfer date
 * @property {Date} createdAt           - Creation time
 * @property {Date} updatedAt           - Updation time
 */

/**
 * Home update parameters
 * @typedef UpdateParams
 * @property {String} addressRoad   - Address road
 * @property {String} structure     - Construction type
 * @property {Number} area          - Useable area
 * @property {String} transferDate  - Transfer date
 * @typedef {LocationDetail & UpdateParams} HomeUpdate
 */

/**
 * Updation result properties
 * @typedef UpdateResult
 * @type {Object}
 * @property {Boolean} updated - Update state
 */

/**
 * Home filter parameters
 * @typedef HomeFilter
 * @type {Object}
 * @property {String} projectCode - Project code
 * @property {String} text        - Search text
 * @property {Number} page        - Fecth page
 * @property {Number} limit       - Fetch limit
 */

/**
 * Home item properties
 * @typedef HomeItem
 * @type {Object}
 * @property {String} id                - Home ID
 * @property {HomeProject} project      - Project detail
 * @property {HomeStructure} structure  - Structure detail
 * @property {HomeLocation} address     - Address detail
 * @property {Date} createdAt           - Creation time
 */

/**
 * Home list properties
 * @typedef HomeList
 * @type {Object}
 * @property {HomeItem[]} items - Home items
 * @property {Number} total     - Total count
 */

/**
 * Deletion result properties
 * @typedef DeleteResult
 * @type {Object}
 * @property {Boolean} deleted - Delete state
 */

export class NonScHomeClient {

  // Request Endpoint
  static endpoint = (
    `${process.env.ADMIN_HOST}/ruejai-backend-admin/api/home/nonsc`
  )

  /**
   * Fetch home details
   * @param {String} homeId - Home ID
   * @returns {Promise<HomeDetail>} Home details
   */
  static async get(homeId) {
    
    // Request URL
    const url = `${this.endpoint}/${homeId}`
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Home List
      const { data } = await axios.get(
        url, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-home.get', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Fetch home list
   * @param {HomeFilter} params
   * @returns {Promise<HomeList>} Home list
   */
  static async list(params = {}) {
    
    // Request URL
    const url = this.endpoint + '/list'
    
    // Default Result
    let result = {
      total: 0, items: []
    }

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Home List
      const { data } = await axios.get(
        url, { headers, params }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-home.list', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request create home
   * @param {HomeCreate} payload - Create parameters
   * @returns {Promise<CreateResult>} Create result
   */
  static async create(payload) {
    
    // Request URL
    const url = this.endpoint
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Home List
      const { data } = await axios.post(
        url, payload, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-home.create', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request update home details
   * @param {Number} homeId       - Home ID
   * @param {HomeUpdate} payload  - Update payload
   * @returns {Promise<UpdateResult>} Update result
   */
  static async update(homeId, payload) {
    
    // Request URL
    const url = `${this.endpoint}/${homeId}`
    
    // Default Result
    let result = null

    try {

      // Validate Params
      if (!homeId || !payload) {
        throw new Error('INVALID_PARAMS')
      }
      
      // Request Headers
      const headers = accessHeaders()

      // Payload Props
      const { addressNo, area } = payload

      // Set Useable Area
      if (area) {
        payload.area = parseFloat(area)
      }

      // Remove Address Number
      if (addressNo) {
        delete payload.addressNo
      }

      // Request Home List
      const { data } = await axios.put(
        url, payload, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-home.update', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request remove home
   * @param {String} homeId - Home ID
   * @returns {Promise<DeleteResult>} Delete result
   */
  static async remove(homeId) {
    
    // Request URL
    const url = `${this.endpoint}/${homeId}`
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Delete Project
      const { data } = await axios.delete(
        url, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-home.remove', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }
}


// WEBPACK FOOTER //
// ./src/clients/BackendAdminClient/nonsc-home.js